/**
 * Reports List - Display and manage all reports
 */

import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Table, Column } from '../UI/Table';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { listReports, downloadReportPDF, downloadReportV1, downloadReportV2, trackReportDownload, listReportJobs, downloadReportDOCX, updateReportStatus } from '../../api/reports';
import { cancelJob, resumeJob } from '../../api/jobs';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useToastStore } from '../../stores/toast';
import { Report, ReportJobStatus } from '../../types/api';
import {
  FileText,
  Download,
  Eye,
  Calendar,
  Search,
  FileCheck,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Activity,
  XCircle,
  Loader2,
  Trash2,
} from 'lucide-react';

interface ReportsListProps {
  onViewReport?: (reportId: string) => void;
  onCreateNew?: () => void;
  viewMode?: 'cards' | 'table';
  queryKeySuffix?: string; // Optional suffix for query key (e.g., 'rfp_proposals')
  listReportsOptions?: Parameters<typeof listReports>[1]; // Options to pass to listReports
  /** When true, show Resume button for checkpointed jobs (admin/tech only) */
  canResumeCheckpointed?: boolean;
  /** Items per page for pagination (default 20, max 100) */
  itemsPerPage?: number;
  /** When set (RFP proposals), hides create / destructive actions based on permission strings */
  proposalWriteAccess?: {
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

const CANCELLABLE_JOB_STATUSES = ['pending', 'queued', 'running', 'checkpointed'] as const;

/** Segment 2 fill (v1 → v2 → save). Backend sends finalize_segment_percent + generation_phase. */
function getReportJobFinalizeSegmentPercent(
  progress: Record<string, unknown>,
  jobStatus: string,
): number {
  const raw = progress?.finalize_segment_percent;
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return Math.min(100, Math.max(0, Math.round(raw)));
  }
  const phase = progress?.generation_phase as string | undefined;
  if (phase === 'v1_draft') return 33;
  if (phase === 'v2_summary') return 66;
  if (phase === 'saving') return 90;
  if (phase === 'complete') return 100;
  if (jobStatus === 'completed') return 100;
  const q = Number(progress?.questions_complete) || 0;
  const t = Number(progress?.total_questions) || 0;
  if (t > 0 && q >= t && jobStatus === 'running') {
    return 50;
  }
  return 0;
}

/**
 * Format date/time in PST timezone
 * Ensures UTC timestamps are converted to Pacific time
 */
const formatDatePST = (dateString: string, options: Intl.DateTimeFormatOptions = {}) => {
  // Ensure the date string has a Z suffix if it doesn't have timezone info
  const dateWithTz = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  return new Date(dateWithTz).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    ...options,
  });
};

export function ReportsList({
  onViewReport,
  onCreateNew,
  viewMode = 'cards',
  queryKeySuffix,
  listReportsOptions,
  canResumeCheckpointed = false,
  itemsPerPage: itemsPerPageProp = 20,
  proposalWriteAccess,
}: ReportsListProps) {
  const { projectId } = useWorkspace();
  const canCreate = proposalWriteAccess?.canCreate !== false;
  const canEdit = proposalWriteAccess?.canEdit !== false;
  const canDelete = proposalWriteAccess?.canDelete !== false;
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const [resumingJobId, setResumingJobId] = useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [archivingReportId, setArchivingReportId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [cancelConfirmJobId, setCancelConfirmJobId] = useState<string | null>(null);
  const [cancelConfirmName, setCancelConfirmName] = useState<string>('');

  // Ensure itemsPerPage is between 1 and 100
  const itemsPerPage = Math.min(100, Math.max(1, itemsPerPageProp));

  // Proposal/workflow status for the chip: prefer backend status (in_review, approved, etc.)
  // MUST be defined before statusCounts useMemo that uses it
  const getReportStatus = (report: Report) => {
    const workflowStatuses = ['archived', 'submitted', 'approved', 'in_review', 'draft', 'rejected'];
    if (report.status && workflowStatuses.includes(report.status)) {
      return report.status;
    }
    // No workflow status (e.g. completed or empty): derive from gaps/questions
    const gaps = report.evidence_gaps_count ?? report.evidence_gaps ?? 0;
    if (gaps > 0) return 'needs_evidence';
    if ((report.answered_questions || 0) < (report.total_questions || 1)) return 'in_progress';
    return report.status || 'completed';
  };

  // Build query key with optional suffix
  const queryKey = queryKeySuffix
    ? ['reports', projectId, queryKeySuffix]
    : ['reports', projectId];

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => listReports(projectId!, listReportsOptions),
    enabled: !!projectId,
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch pending report generation jobs
  const { data: reportJobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['report-jobs', projectId],
    queryFn: () => listReportJobs(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000, // Refresh every 5s for active jobs
  });

  // Combine reports and pending jobs; align jobs with listReportsOptions (e.g. exclude workplan jobs in proposal management)
  const allItems = React.useMemo(() => {
    const reportItems = (reports || []).map(r => ({ type: 'report' as const, data: r }));
    const excludeType = listReportsOptions?.exclude_report_type;
    const onlyType = listReportsOptions?.report_type;
    const jobItems = (reportJobs || [])
      .filter((j: ReportJobStatus) => {
        const jobType = j.input_data?.report_type || j.input_data?.metadata?.frontend_type || '';
        if (excludeType && jobType === excludeType) return false; // e.g. hide workplan_generation in Current Proposals
        if (onlyType && jobType !== onlyType) return false; // e.g. show only workplan_generation in Current Workplans
        return true;
      })
      .map((j: ReportJobStatus) => ({ type: 'job' as const, data: j }));
    const combined = [...jobItems, ...reportItems]; // Show pending jobs first
    return combined;
  }, [reports, reportJobs, listReportsOptions?.exclude_report_type, listReportsOptions?.report_type]);

  const filteredReports = React.useMemo(() => {
    if (!allItems) return [];

    return allItems.filter((item) => {
      const name = item.type === 'report'
        ? (item.data.name || item.data.client_name)
        : (item.data as ReportJobStatus).input_data?.name || (item.data as ReportJobStatus).input_data?.client_name || 'Pending Report';
      const type = item.type === 'report'
        ? item.data.report_type
        : (item.data as ReportJobStatus).input_data?.report_type ||
        (item.data as ReportJobStatus).input_data?.metadata?.frontend_type ||
        'compliance';

      const matchesSearch =
        name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filterType === 'all' || type === filterType;
      // Keep filtering consistent with status chips/counts:
      // - Reports use derived workflow status (getReportStatus)
      // - "completed" should behave as "draft" in UI filters
      const itemStatus = item.type === 'report'
        ? (() => {
          const derived = getReportStatus(item.data as Report);
          return derived === 'completed' ? 'draft' : derived;
        })()
        : (item.data as ReportJobStatus).status;
      const matchesStatus = filterStatus === 'all'
        ? itemStatus !== 'archived'
        : itemStatus === filterStatus;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [allItems, searchTerm, filterType, filterStatus]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = React.useMemo(() => {
    const startIdx = currentPage * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return filteredReports.slice(startIdx, endIdx);
  }, [filteredReports, currentPage, itemsPerPage]);

  // Reset to page 0 when filters change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, filterType, filterStatus]);

  // Notify when a report/proposal generation job completes
  const prevJobStatusesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const jobs = reportJobs || [];
    const prev = prevJobStatusesRef.current;
    const inProgressStatuses = ['pending', 'queued', 'running', 'checkpointed'];
    for (const j of jobs) {
      const id = String(j.id);
      const status = j.status || '';
      const prevStatus = prev.get(id);
      if (
        prevStatus &&
        inProgressStatuses.includes(prevStatus) &&
        status === 'completed'
      ) {
        const name =
          j.input_data?.name ||
          j.input_data?.client_name ||
          j.input_data?.metadata?.opportunity_title ||
          'Report';
        const reportType =
          j.input_data?.report_type ||
          j.input_data?.metadata?.frontend_type ||
          '';
        const isProposal =
          reportType === 'proposal' ||
          reportType === 'compliance' ||
          (reportType && reportType.toLowerCase().includes('proposal'));
        addToast({
          type: 'success',
          title: isProposal ? 'Proposal generated' : 'Report ready',
          message: `"${String(name).slice(0, 50)}${String(name).length > 50 ? '…' : ''}" is ready to view.`,
          duration: 6000,
        });
        // Refresh navbar notification bell (unread count + list); backend creates job_completion in-app notification
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
      prev.set(id, status);
    }
    const ids = new Set(jobs.map((j: ReportJobStatus) => String(j.id)));
    for (const id of prev.keys()) {
      if (!ids.has(id)) prev.delete(id);
    }
  }, [reportJobs, addToast, queryClient]);

  // Calculate accurate status counts from all items (not just filtered/paginated)
  const statusCounts = React.useMemo(() => {
    const counts = {
      draft: 0,
      in_review: 0,
      in_progress: 0,
      needs_evidence: 0,
      approved: 0,
      submitted: 0,
      archived: 0,
      rejected: 0,
      completed: 0,
      generated: 0,
      generating: 0,
      pending: 0,
      queued: 0,
      running: 0,
      checkpointed: 0,
    };

    allItems?.forEach((item) => {
      if (item.type === 'report') {
        const status = getReportStatus(item.data);
        const normalizedStatus = status === 'completed' ? 'draft' : status;
        if (normalizedStatus in counts) {
          counts[normalizedStatus as keyof typeof counts]++;
        }
      } else {
        const jobStatus = (item.data as ReportJobStatus).status;
        if (jobStatus === 'running' || jobStatus === 'queued' || jobStatus === 'pending') {
          counts.generating++;
        }
        if (jobStatus in counts) {
          counts[jobStatus as keyof typeof counts]++;
        }
      }
    });

    return counts;
  }, [allItems]);

  const handleDownload = async (reportId: string, format: 'v1' | 'v2' | 'pdf' | 'docx') => {
    const formatLabel = format.toUpperCase();
    setDownloadingReportId(`${reportId}-${format}`);
    try {
      // Track download
      trackReportDownload(reportId, format).catch(err => console.error('Track failed:', err));

      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        addToast({
          type: 'success',
          title: 'PDF downloaded',
          message: 'Report PDF has been downloaded successfully.',
          duration: 4000,
        });
      } else if (format === 'docx') {
        const blob = await downloadReportDOCX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}.docx`;
        a.click();
        window.URL.revokeObjectURL(url);
        addToast({
          type: 'success',
          title: 'DOCX downloaded',
          message: 'Report Word document has been downloaded successfully.',
          duration: 4000,
        });
      } else {
        const content = format === 'v1'
          ? await downloadReportV1(reportId)
          : await downloadReportV2(reportId, false); // view=false for actual download
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}_${format}.md`;
        a.click();
        window.URL.revokeObjectURL(url);
        addToast({
          type: 'success',
          title: 'Download complete',
          message: `Report (${formatLabel}) has been downloaded successfully.`,
          duration: 4000,
        });
      }
    } catch (error: any) {
      console.error('Download failed:', error);
      const message = error?.response?.data?.detail ?? error?.message ?? 'Unknown error';
      addToast({
        type: 'error',
        title: `${formatLabel} download failed`,
        message: typeof message === 'string' ? message : 'Could not download the report. Please try again.',
        duration: 6000,
      });
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleArchiveReport = async (reportId: string, reportName: string) => {
    if (!window.confirm(`Delete "${reportName}"? This will archive the report.`)) return;
    setArchivingReportId(reportId);
    try {
      await updateReportStatus(reportId, 'archived');
      addToast({
        type: 'success',
        title: 'Report deleted',
        message: `"${reportName}" has been archived.`,
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey });
    } catch (error: any) {
      console.error('[ReportsList] Archive failed:', error);
      addToast({
        type: 'error',
        title: 'Delete failed',
        message: error?.message || 'Could not archive the report.',
        duration: 6000,
      });
    } finally {
      setArchivingReportId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelConfirmJobId) return;
    const id = cancelConfirmJobId;
    setCancelConfirmJobId(null);
    setCancellingJobId(id);
    try {
      await cancelJob(id);
      await queryClient.invalidateQueries({ queryKey: ['report-jobs', projectId] });
      await queryClient.invalidateQueries({ queryKey });
    } catch (err: any) {
      console.error('[ReportsList] Cancel job failed:', err);
      addToast({
        type: 'error',
        title: 'Cancel failed',
        message: err?.message || 'Failed to cancel job',
        duration: 6000,
      });
    } finally {
      setCancellingJobId(null);
    }
  };

  const reportTypeColors = {
    compliance: 'from-blue-500 to-blue-600',
    architecture: 'from-purple-500 to-purple-600',
    risk: 'from-red-500 to-red-600',
    workflow: 'from-green-500 to-green-600',
    custom: 'from-gray-500 to-gray-600',
    rfp_proposal: 'from-indigo-500 to-indigo-600',
    // MineScope India
    monthly_ec: 'from-emerald-500 to-emerald-600',
    six_monthly_ec: 'from-teal-500 to-teal-600',
    water_quality: 'from-cyan-500 to-cyan-600',
    form_m: 'from-amber-500 to-amber-600',
    cto_quarterly: 'from-orange-500 to-orange-600',
    exceedance_alert: 'from-red-500 to-red-600',
    caaqms_monthly: 'from-indigo-500 to-indigo-600',
    annual_environmental: 'from-purple-500 to-purple-600',
    fc_compliance: 'from-green-500 to-green-600',
    // PFAS/MARPAC/Keystone
    quarterly_monitoring: 'from-blue-500 to-blue-600',
    annual_report: 'from-purple-500 to-purple-600',
    environmental_impact: 'from-green-500 to-green-600',
    client_deliverable: 'from-indigo-500 to-indigo-600',
  };

  const reportTypeIcons = {
    compliance: FileCheck,
    architecture: FileText,
    risk: AlertCircle,
    workflow: TrendingUp,
    custom: FileText,
    rfp_proposal: FileText,
    monthly_ec: Calendar,
    six_monthly_ec: Calendar,
    water_quality: FileCheck,
    form_m: FileText,
    cto_quarterly: Clock,
    exceedance_alert: AlertCircle,
    caaqms_monthly: Activity,
    annual_environmental: Calendar,
    fc_compliance: CheckCircle,
    quarterly_monitoring: Calendar,
    annual_report: Calendar,
    environmental_impact: TrendingUp,
    client_deliverable: FileText,
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-theme-text-secondary">Select a project to view reports</p>
      </Card>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading reports..." />;
  }

  return (
    <div className="space-y-6">
      {/* Compact Status Summary at Top */}
      {allItems && allItems.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap" data-testid="status-summary-chips">
              {statusCounts.checkpointed > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900 border-2 border-red-300 dark:border-red-700 rounded-full text-sm">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-300" />
                  <span className="font-semibold text-red-700 dark:text-red-200">Checkpointed</span>
                  <span className="font-bold text-base text-red-900 dark:text-white">{statusCounts.checkpointed}</span>
                </div>
              )}
              {statusCounts.draft > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-200 dark:bg-slate-800 border-2 border-slate-400 dark:border-slate-600 rounded-full text-sm">
                  <FileText className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Draft</span>
                  <span className="font-bold text-base text-slate-900 dark:text-white">{statusCounts.draft}</span>
                </div>
              )}
              {statusCounts.in_review > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900 border-2 border-blue-400 dark:border-blue-600 rounded-full text-sm">
                  <Eye className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                  <span className="font-semibold text-blue-700 dark:text-blue-200">In Review</span>
                  <span className="font-bold text-base text-blue-900 dark:text-white">{statusCounts.in_review}</span>
                </div>
              )}
              {statusCounts.approved > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900 border-2 border-green-400 dark:border-green-600 rounded-full text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-300" />
                  <span className="font-semibold text-green-700 dark:text-green-200">Approved</span>
                  <span className="font-bold text-base text-green-900 dark:text-white">{statusCounts.approved}</span>
                </div>
              )}
              {statusCounts.submitted > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-teal-100 dark:bg-teal-900 border-2 border-teal-400 dark:border-teal-600 rounded-full text-sm">
                  <FileCheck className="h-4 w-4 text-teal-600 dark:text-teal-300" />
                  <span className="font-semibold text-teal-700 dark:text-teal-200">Submitted</span>
                  <span className="font-bold text-base text-teal-900 dark:text-white">{statusCounts.submitted}</span>
                </div>
              )}
              {statusCounts.needs_evidence > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900 border-2 border-yellow-400 dark:border-yellow-600 rounded-full text-sm">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-300" />
                  <span className="font-semibold text-yellow-700 dark:text-yellow-200">Needs Evidence</span>
                  <span className="font-bold text-base text-yellow-900 dark:text-white">{statusCounts.needs_evidence}</span>
                </div>
              )}
              {statusCounts.archived > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-800 border-2 border-gray-400 dark:border-gray-600 rounded-full text-sm">
                  <XCircle className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  <span className="font-semibold text-gray-700 dark:text-gray-200">Archived</span>
                  <span className="font-bold text-base text-gray-900 dark:text-white">{statusCounts.archived}</span>
                </div>
              )}
              {statusCounts.rejected > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900 border-2 border-red-400 dark:border-red-600 rounded-full text-sm">
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-300" />
                  <span className="font-semibold text-red-700 dark:text-red-200">Rejected</span>
                  <span className="font-bold text-base text-red-900 dark:text-white">{statusCounts.rejected}</span>
                </div>
              )}
              {statusCounts.generating > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900 border-2 border-purple-400 dark:border-purple-600 rounded-full text-sm">
                  <Loader2 className="h-4 w-4 text-purple-600 dark:text-purple-300 animate-spin" />
                  <span className="font-semibold text-purple-700 dark:text-purple-200">Generating</span>
                  <span className="font-bold text-base text-purple-900 dark:text-white">{statusCounts.generating}</span>
                </div>
              )}
              <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-full text-sm font-bold shadow-sm">
                <span className="text-blue-700 dark:text-blue-300">Total</span>
                <span className="text-base text-blue-900 dark:text-white">{allItems.length}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Search and Filters */}
      <Card className="">
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-disabled z-10 dark:text-black" />
              <Input
                placeholder="Search reports by name or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 dark:text-black"
              />
            </div>
            {/* <Button onClick={onCreateNew} className="whitespace-nowrap">
              <FileText className="h-4 w-4" />
              New Report
            </Button> */}
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                {/* <Filter className="inline h-4 w-4 mr-1" /> */}
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-paper dark:bg-gray-800 text-theme-text focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">All Types</option>
                {/* Dynamic options from reports and jobs data */}
                {Array.from(new Set([
                  ...(reports?.map(r => r.report_type).filter(Boolean) || []),
                  ...(reportJobs?.map(j => j.input_data?.report_type || j.input_data?.metadata?.frontend_type).filter(Boolean) || [])
                ])).map((type: string) => (
                  <option key={type} value={type}>
                    {/* {type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l)} */}
                    {type?.replace(/_/g, ' ').replace(/\brfp\b/gi, 'RFP').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-paper dark:bg-gray-800 text-theme-text focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">All Status</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="pending">Pending</option>
                <option value="checkpointed">Checkpointed</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="submitted">Submitted</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Stats — positioned above the grid so counts describe the cards below them */}
      {filteredReports.length > 0 && (
        <Card>
          <div className="flex items-center justify-between flex-wrap gap-4" data-testid="filtered-summary-stats">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-theme-text-disabled" />
                <span className="text-sm font-medium text-theme-text">
                  {filteredReports.length} Total Filtered
                </span>
              </div>
              {paginatedReports.filter(item => item.type === 'job').length > 0 && (
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-theme-text-secondary dark:text-black">
                    {paginatedReports.filter(item => item.type === 'job').length} Generating
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                <span className="text-sm text-theme-text-secondary">
                  {paginatedReports.filter(item =>
                    item.type === 'report' && (item.data as Report).status === 'approved'
                  ).length} Approved (page)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                <span className="text-sm text-theme-text-secondary">
                  {paginatedReports.filter(item =>
                    item.type === 'report' && (item.data as Report).status === 'in_review'
                  ).length} In Review (page)
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reports-grid">
        {paginatedReports.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-theme-text-disabled mx-auto mb-4" />
                <p className="text-theme-text-secondary">{filteredReports.length === 0 ? 'No reports found' : 'No reports on this page'}</p>
                {filteredReports.length === 0 && canCreate && (
                  <Button onClick={onCreateNew} className="mt-4">
                    Create Your First Report
                  </Button>
                )}
              </div>
            </Card>
          </div>
        ) : (
          paginatedReports.map((item) => {
            if (item.type === 'job') {
              const job = item.data as ReportJobStatus;
              const displayName = job.input_data?.name || job.input_data?.client_name || 'Pending Report';
              const reportType = job.input_data?.metadata?.frontend_type || job.input_data?.report_type || 'compliance';
              const formatType = (text: string) => {
                const specialWords = ['rfp']

                return text
                  .split('_')
                  .join(' ')
                  .split(' ')
                  .map(word =>
                    specialWords.includes(word.toLowerCase())
                      ? word.toUpperCase()
                      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(' ');
              };

              const templateName = job.input_data?.metadata?.template_name || formatType(reportType);
              const progress = job.progress_data || {};

              // Extract progress fields from progress_data (report jobs store details here)
              const questionsComplete = progress.questions_complete || 0;
              const totalQuestions = progress.total_questions || 0;

              // Calculate elapsed time (time since job started)
              const elapsedSeconds = job.started_at
                ? Math.floor((Date.now() - new Date(job.started_at).getTime()) / 1000)
                : 0;

              // Get ETA from progress_data
              const etaSeconds = progress.eta_seconds || null;
              const finalizeEtaSeconds = (progress.finalize_eta_seconds as number) || 0;

              // Total ETA = remaining S1 time + remaining S2 time
              const totalEtaSeconds = (etaSeconds && etaSeconds > 0 ? etaSeconds : 0) + finalizeEtaSeconds;

              // Get current message and stage
              const currentMessage = progress.status_message || null;
              const currentStage = progress.current_stage || null;

              // Segment 1: per-question answers (0–100% of this segment)
              let questionsSegmentPct =
                totalQuestions > 0
                  ? Math.min(100, Math.round((questionsComplete / totalQuestions) * 100))
                  : job.status === 'completed'
                    ? 100
                    : 0;
              if (job.status === 'completed' && questionsSegmentPct < 100) {
                questionsSegmentPct = 100;
              }

              // Segment 2: research draft (v1) → summary (v2) → save (backend-driven)
              const finalizeSegmentPct = getReportJobFinalizeSegmentPercent(
                progress as Record<string, unknown>,
                job.status,
              );

              const answersSegmentComplete = questionsSegmentPct >= 100;
              const finalizeSegmentComplete = finalizeSegmentPct >= 100;
              const draftSegmentNotStarted = !answersSegmentComplete;
              const draftSegmentProcessing = answersSegmentComplete && !finalizeSegmentComplete;

              const IconComponent = reportTypeIcons[reportType as keyof typeof reportTypeIcons] || FileText;
              const gradientColor = reportTypeColors[reportType as keyof typeof reportTypeColors] || reportTypeColors.custom;

              return (
                <Card key={job.id} className="hover:shadow-lg transition-shadow border-2 border-blue-200 ">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className={`p-3 bg-gradient-to-br ${gradientColor} rounded-lg shadow-md`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-theme-text truncate">
                          {displayName}
                        </h3>
                        <p className="text-sm text-theme-text-secondary capitalize">
                          {templateName}
                        </p>
                      </div>
                    </div>

                    {/* Two-segment progress: answers vs draft/summary/save */}
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs gap-2">
                          <span className="text-theme-text-secondary font-medium">
                            {answersSegmentComplete ? (
                              <>Answers — <span className="text-green-600 dark:text-green-500">Done</span></>
                            ) : (
                              <>Answers — <span className="text-blue-600 dark:text-blue-400">(processing)</span></>
                            )}
                          </span>
                          <span
                            className={`font-medium shrink-0 ${answersSegmentComplete ? 'text-green-600 dark:text-green-500' : 'text-theme-text'}`}
                          >
                            {answersSegmentComplete ? 'Done' : `${questionsSegmentPct}%`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${answersSegmentComplete ? 'bg-green-600 dark:bg-green-500' : 'bg-blue-600 dark:bg-blue-500'
                              }`}
                            style={{ width: `${questionsSegmentPct}%` }}
                          />
                        </div>
                        <div className="text-xs text-theme-text-secondary">
                          {questionsComplete} of {totalQuestions} questions complete
                          {progress.current_question && (
                            <div className="mt-1 italic">Current: {String(progress.current_question).substring(0, 50)}...</div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs gap-2">
                          <span className="text-theme-text-secondary font-medium">
                            {draftSegmentNotStarted ? (
                              <>
                                Draft &amp; summary —{' '}
                                <span className="text-gray-500 dark:text-gray-400">(not started)</span>
                              </>
                            ) : finalizeSegmentComplete ? (
                              <>
                                Draft &amp; summary —{' '}
                                <span className="text-green-600 dark:text-green-500">Done</span>
                              </>
                            ) : (
                              <>
                                Draft &amp; summary —{' '}
                                <span className="text-indigo-600 dark:text-indigo-400">(processing)</span>
                              </>
                            )}
                          </span>
                          <span
                            className={`font-medium shrink-0 ${finalizeSegmentComplete
                                ? 'text-green-600 dark:text-green-500'
                                : draftSegmentNotStarted
                                  ? 'text-gray-500 dark:text-gray-400'
                                  : 'text-theme-text'
                              }`}
                          >
                            {finalizeSegmentComplete ? 'Done' : draftSegmentNotStarted ? '—' : `${finalizeSegmentPct}%`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${finalizeSegmentComplete
                                ? 'bg-green-600 dark:bg-green-500'
                                : draftSegmentProcessing
                                  ? 'bg-indigo-600 dark:bg-indigo-500'
                                  : 'bg-transparent'
                              }`}
                            style={{ width: `${finalizeSegmentPct}%` }}
                          />
                        </div>
                        <p className="text-[11px] text-theme-text-muted leading-snug">
                          Research draft (v1), executive summary (v2), then save. Starts after all answers are complete.
                        </p>
                      </div>
                    </div>

                    {/* Current Stage/Message */}
                    {currentMessage && (
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                        <div className="text-xs text-blue-600 font-medium mb-1">
                          {currentStage ? `Stage: ${currentStage.replace(/_/g, ' ')}` : 'Processing'}
                        </div>
                        <div className="text-xs text-theme-text italic">
                          {currentMessage}
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50  p-3 rounded-lg">
                        <div className="text-xs text-theme-text-secondary mb-1">Status</div>
                        <div className="text-sm font-bold text-theme-text capitalize">
                          {job.status}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs text-theme-text-muted mb-1">Elapsed</div>
                        <div className="text-sm font-bold text-theme-text">
                          {elapsedSeconds > 0
                            ? `${Math.floor(elapsedSeconds / 60)}m ${Math.round(elapsedSeconds % 60)}s`
                            : '-'}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs text-theme-text-muted mb-1">Total ETA</div>
                        <div className="text-sm font-bold text-theme-text">
                          {totalEtaSeconds > 0
                            ? `${Math.floor(totalEtaSeconds / 60)}m ${Math.round(totalEtaSeconds % 60)}s`
                            : '-'}
                        </div>
                      </div>
                    </div>

                    {/* Status Badges */}
                    {/* <div className="flex flex-wrap gap-2">
                      <Badge
                        variant={
                          job.status === 'running' ? 'info' :
                            job.status === 'queued' ? 'default' :
                              job.status === 'pending' ? 'default' :
                                'warning'
                        }
                      >
                        {job.status === 'running' && <Activity className="h-3 w-3 mr-1 animate-pulse" />}
                        {job.status === 'queued' && <Clock className="h-3 w-3 mr-1" />}
                        {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </Badge>
                      {elapsedSeconds > 0 && (
                        <Badge variant="default" className="bg-gray-100 ">
                          <Clock className="h-3 w-3 mr-1" />
                          {Math.floor(elapsedSeconds / 60)}m {Math.round(elapsedSeconds % 60)}s
                        </Badge>
                      )}
                      {totalEtaSeconds > 0 && (
                     <Badge variant="default" className="bg-blue-100 text-blue-700">
                    ⏱ Total ETA: {Math.floor(totalEtaSeconds / 60)}m {Math.round(totalEtaSeconds % 60)}s
                    </Badge>
                      )}
                      {(progress.cache_hits || 0) > 0 && (
                        <Badge variant="success" className="bg-green-100  text-green-700 dark:text-green-300">
                          {progress.cache_hits} cached
                        </Badge>
                      )}
                    </div> */}

                    {/* Metadata */}
                    <div className="text-xs text-theme-text-secondary space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        Started: {formatDatePST(job.started_at || job.created_at)}
                      </div>
                      {progress.current_section && (
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          Section: {progress.current_section}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-theme-border">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-1"
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Generating...
                      </Button>
                      {canResumeCheckpointed && canEdit && job.status === 'checkpointed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50  border-blue-200"
                          disabled={resumingJobId === String(job.id)}
                          onClick={async () => {
                            const id = String(job.id);
                            if (!window.confirm('Resume this checkpointed job? It will continue processing remaining questions.')) return;
                            setResumingJobId(id);
                            try {
                              await resumeJob(id);
                              await queryClient.invalidateQueries({ queryKey: ['report-jobs', projectId] });
                              await queryClient.invalidateQueries({ queryKey });
                            } catch (err: any) {
                              console.error('[ReportsList] Resume job failed:', err);
                              alert(err?.message || 'Failed to resume job');
                            } finally {
                              setResumingJobId(null);
                            }
                          }}
                        >
                          {resumingJobId === String(job.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Activity className="h-4 w-4 mr-2" />
                          )}
                          Resume
                        </Button>
                      )}
                      {canEdit && CANCELLABLE_JOB_STATUSES.includes(job.status as typeof CANCELLABLE_JOB_STATUSES[number]) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50  border-red-200"
                          disabled={cancellingJobId === String(job.id)}
                          onClick={() => {
                            setCancelConfirmJobId(String(job.id));
                            setCancelConfirmName(displayName);
                          }}
                        >
                          {cancellingJobId === String(job.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <XCircle className="h-4 w-4 mr-2" />
                          )}
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            } else {
              const report = item.data as Report;
              const reportType = report.report_type || 'custom';
              const IconComponent = reportTypeIcons[reportType as keyof typeof reportTypeIcons] || FileText;
              const gradientColor = reportTypeColors[reportType as keyof typeof reportTypeColors] || reportTypeColors.custom;
              const meta = report.meta || (report as any).metadata || {};
              const templateMeta = meta?.template_result?.metadata || {};
              const displayName = report.name || meta.proposal_name || meta.name || report.client_name || 'Untitled Report';
              const evidenceGapsCount = report.evidence_gaps_count ?? report.evidence_gaps ?? 0;
              const organizationName = templateMeta.organization_name || meta.client_name;
              const formatReportType = (type?: string) => {
                if (!type) return '';

                const specialWords = ['rfp'];

                return type
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map(word =>
                    specialWords.includes(word.toLowerCase())
                      ? word.toUpperCase()
                      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(' ');
              };

              const actualStatus = getReportStatus(report);
              const displayStatus = actualStatus === 'completed' ? 'draft' : actualStatus;
              const statusLabel = displayStatus.replace(/_/g, ' ');
              return (
                <Card key={report.id} className="hover:shadow-lg transition-shadow ">
                  <div className="space-y-4">
                    {/* Status chip: own row above and to the left. Each status gets a distinct
                        color + icon combo so Draft (slate/FileText) and In Review (blue/Eye)
                        can't be confused at a glance. */}
                    <div className="flex justify-start">
                      <Badge
                        variant="default"
                        className={clsx(
                          'inline-flex items-center gap-1.5 text-sm font-bold capitalize border-2 shadow-sm px-3 py-1.5',
                          displayStatus === 'in_review' && 'bg-blue-100 text-blue-800 dark:text-blue-200 border-blue-400 dark:border-blue-600',
                          (displayStatus === 'approved' || displayStatus === 'submitted') && 'bg-green-100 text-green-800 border-green-400 dark:border-green-600',
                          (displayStatus === 'needs_evidence' || displayStatus === 'in_progress') && 'bg-amber-100 text-amber-800 border-amber-400 dark:border-amber-600',
                          displayStatus === 'draft' && 'bg-slate-200 text-slate-800 dark:text-slate-100 border-slate-400 dark:border-slate-500',
                          displayStatus === 'archived' && 'bg-gray-200 text-gray-600 border-gray-400',
                          displayStatus === 'rejected' && 'bg-red-100 text-red-700 border-red-400'
                        )}
                      >
                        {displayStatus === 'in_review' && <Eye className="h-3.5 w-3.5" />}
                        {(displayStatus === 'approved' || displayStatus === 'submitted') && <CheckCircle className="h-3.5 w-3.5" />}
                        {(displayStatus === 'needs_evidence' || displayStatus === 'in_progress') && <AlertCircle className="h-3.5 w-3.5" />}
                        {displayStatus === 'draft' && <FileText className="h-3.5 w-3.5" />}
                        {(displayStatus === 'archived' || displayStatus === 'rejected') && <XCircle className="h-3.5 w-3.5" />}
                        {statusLabel}
                      </Badge>
                    </div>
                    {/* Header: icon + title (full width for title) */}
                    <div className="flex items-start gap-3">
                      <div className={`p-3 bg-gradient-to-br ${gradientColor} rounded-lg shadow-md flex-shrink-0`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-theme-text truncate" title={displayName}>
                          {displayName}
                        </h3>
                        <p className="text-sm text-theme-text-secondary capitalize" title={formatReportType(report.report_type)}>
                          {formatReportType(report.report_type)}
                          {organizationName && organizationName !== displayName && (
                            <span className="block truncate mt-0.5 text-theme-text-secondary" title={organizationName}>{organizationName}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Stats - one row of 3 (Questions, Confidence, Citations) - use exact API fields */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50  p-3 rounded-lg">
                        <div className="text-xs text-theme-text-muted mb-1 dark:text-black">Questions</div>
                        <div className="text-lg font-bold text-theme-text dark:text-black">
                          {report.answered_questions || 0}/{report.total_questions || 0}
                        </div>
                      </div>
                      <div className="bg-gray-50  p-3 rounded-lg">
                        <div className="text-xs text-theme-text-muted mb-1 dark:text-black">Confidence</div>
                        <div className="text-lg font-bold text-theme-text dark:text-black">
                          {report.avg_confidence != null ? `${Math.round(report.avg_confidence * 100)}%` : '-'}
                        </div>
                      </div>
                      <div className="bg-gray-50  p-3 rounded-lg">
                        <div className="text-xs text-theme-text-muted mb-1 dark:text-black">Citations</div>
                        <div className="text-lg font-bold text-theme-text dark:text-black">
                          {report.total_citations || 0}
                        </div>
                      </div>
                    </div>

                    {/* Extra badges: Low Confidence and Evidence Gaps count (separate from proposal status) */}
                    <div className="flex flex-wrap gap-2">
                      {report.avg_confidence != null && report.avg_confidence < 0.7 && (
                        <Badge variant="warning" className="dark:text-white">Low Confidence</Badge>
                      )}
                      {evidenceGapsCount > 0 && (
                        <Badge variant="warning">{evidenceGapsCount} Evidence Gap{evidenceGapsCount !== 1 ? 's' : ''}</Badge>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="text-xs text-theme-text-secondary space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        Created: {formatDatePST(report.created_at, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>
                      {report.updated_at && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Updated: {formatDatePST(report.updated_at, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      {(() => {
                        const isStillGenerating =
                          report.source_type === 'ai' &&
                          (report.total_questions ?? 0) === 0 &&
                          (report.answered_questions ?? 0) === 0;
                        return (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewReport?.(report.id)}
                            className="flex-1"
                            disabled={isStillGenerating}
                            title={isStillGenerating ? 'Report is still being generated — please wait' : undefined}
                          >
                            {isStillGenerating ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4 mr-2" />
                            )}
                            {isStillGenerating ? 'Generating…' : 'View'}
                          </Button>
                        );
                      })()}
                      {/* PDF Download */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(report.id, 'pdf')}
                        title="Download PDF version of report"
                        disabled={downloadingReportId === `${report.id}-pdf`}
                      >
                        {downloadingReportId === `${report.id}-pdf` ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        PDF
                      </Button>
                      {/* DOCX Download */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(report.id, 'docx')}
                        title="Download Word document version of report"
                        disabled={downloadingReportId === `${report.id}-docx`}
                      >
                        {downloadingReportId === `${report.id}-docx` ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        DOCX
                      </Button>
                      {/* Delete (archive) */}
                      {canDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArchiveReport(report.id, displayName)}
                        title="Delete this report (archive)"
                        disabled={archivingReportId === report.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50  border-red-200"
                      >
                        {archivingReportId === report.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            }
          })
        )}
      </div>

      {/* Pagination Controls */}
      {filteredReports.length > itemsPerPage && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50  border border-blue-200 dark:from-blue-950 dark:to-indigo-950">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-theme-text dark:text-white">
                Page {currentPage + 1} of {totalPages} • Showing {(currentPage * itemsPerPage) + 1}-{Math.min((currentPage + 1) * itemsPerPage, filteredReports.length)} of {filteredReports.length}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-3 py-1 rounded text-sm font-medium transition-all ${currentPage > 0
                  ? 'bg-blue-500 hover:bg-blue-600 text-white dark:text-white cursor-pointer'
                  : 'bg-theme-surface-hover text-theme-text-disabled dark:text-white cursor-not-allowed'
                  }`}
              >
                ← Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className={`px-3 py-1 rounded text-sm font-medium transition-all ${currentPage < totalPages - 1
                  ? 'bg-blue-500 hover:bg-blue-600 text-white dark:text-white cursor-pointer'
                  : 'bg-theme-surface-hover text-theme-text-disabled dark:text-white cursor-not-allowed'
                  }`}
              >
                Next →
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirmJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setCancelConfirmJobId(null)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200">
            {/* Icon */}
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Cancel Report Generation?
            </h3>
            {/* Message */}
            <p className="text-sm text-gray-500 text-center mb-1">
              You are about to cancel:
            </p>
            <p className="text-sm font-medium text-gray-800 text-center mb-4 truncate px-2">
              "{cancelConfirmName}"
            </p>
            <p className="text-sm text-red-600 text-center mb-6 bg-red-50 rounded-lg py-2 px-3">
              ⚠️ All progress will be lost and cannot be recovered.
            </p>
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setCancelConfirmJobId(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                No, Keep Running
              </button>
              <button
                onClick={handleConfirmCancel}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                Yes, Cancel It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
